import { GameBetValues, StakeValidationIssue } from "services/bonus/type"

import { TranslateFn } from "./bonus2.helpers"

/**
 * SOFT-736 — the free-spins stake, and the arithmetic that keeps it honest.
 *
 * A stake has to be one of the provider's own rungs for the game, in the player's currency. The
 * two unit systems that meet here are the whole reason this file exists:
 *
 * - the LADDERS are the provider's MAJOR units (`20` = 20 CLP, `0.02` = 2 US cents);
 * - the platform stores every currency in MINOR units — ×100 for ALL of them, regardless of what
 *   ISO 4217 says about that currency's decimals (a CLP wallet renders `CLP$104,104.88`).
 *
 * So a stake picked as `20` is stored as `2000`, and an operator who types `10` meaning 10 CLP
 * stores 0.10 CLP — a hundredfold error the form would happily display as correct. Every
 * conversion goes through this file so that factor lives in exactly one place.
 */

/** Ladder tier (major units) → the stored stake (minor units). */
export const tierToMinor = (tier: number): number => Math.round(tier * 100)

/** Stored stake (minor units) → the ladder's units, for comparison and display. */
export const minorToTier = (minor: number): number => minor / 100

/** Money read from JSON compares at the cent, not the bit. */
export const isSameTier = (a: number, b: number): boolean => Math.abs(a - b) < 1e-9

/** A stake as the operator must see it: the amount WITH its currency, never the raw integer. */
export const formatStake = (minor: number | null | undefined, coin: string): string => {
  if (minor === null || minor === undefined) return "—"
  const major = minorToTier(Number(minor))
  // Tiers run from fractions of a US cent (0.01) to whole thousands of pesos, so the small end
  // needs four decimals while the ordinary case reads best as plain money. Trailing zeros are
  // trimmed only in the four-decimal branch — never from a whole number, which would turn
  // "100" into "1".
  const text = Math.abs(major) < 1 ? major.toFixed(4).replace(/0+$/, "").replace(/\.$/, "") : major.toFixed(2)
  return `${text} ${coin.toUpperCase()}`
}

/** A ladder tier as the picker shows it. Same rule as a stored stake, minus the conversion. */
export const formatTier = (tier: number, coin: string): string => formatStake(tierToMinor(tier), coin)

/**
 * The tiers a stake may use for `coin` across EVERY selected game.
 *
 * The backend validates the stake against all of them, so offering the union would offer values
 * the save then rejects. `null` = at least one game has no ladder for this currency, which is a
 * different state from "the intersection is empty" and has a different fix (sync, not re-pick).
 */
export const tiersForCurrency = (ladders: GameBetValues[], coin: string): number[] | null => {
  if (ladders.length === 0) return null
  const upper = coin.toUpperCase()
  const perGame = ladders.map((l) => l.bet_values?.[upper])
  if (perGame.some((tiers) => !Array.isArray(tiers) || tiers.length === 0)) return null

  const [first, ...rest] = perGame as number[][]
  const shared = first.filter((tier) => rest.every((tiers) => tiers.some((t) => isSameTier(t, tier))))
  return shared.sort((a, b) => a - b)
}

/** A currency list as the form compares and renders it: upper-cased, deduped, ordered. Every set
 *  of coins passes through here, so two of them can never differ by casing or order alone. */
export const normalizeCoins = (coins: string[]): string[] =>
  Array.from(new Set(coins.map((c) => (c || "").toUpperCase()).filter(Boolean))).sort((a, b) => a.localeCompare(b))

/** Currencies at least one selected game has no ladder for — what the Sync button is for. */
export const missingCurrencies = (ladders: GameBetValues[]): string[] =>
  normalizeCoins(ladders.flatMap((l) => l.missing_fiat ?? []))

/** The tiers nearest a rejected value — so a wrong stake is answered with the right ones. */
export const nearestTiers = (tiers: number[], value: number, count = 5): number[] =>
  [...tiers]
    .sort((a, b) => Math.abs(a - value) - Math.abs(b - value))
    .slice(0, count)
    .sort((a, b) => a - b)

// ── validator issues ────────────────────────────────────────────────────────────

export const STAKE_ISSUE_CODES = {
  GAMES_REQUIRED: "FREE_SPINS_GAMES_REQUIRED",
  STAKE_REQUIRED: "FREE_SPINS_STAKE_REQUIRED",
  NOT_A_TIER: "FREE_SPINS_BET_VALUE_NOT_A_TIER",
  NOT_SYNCED: "GAME_BET_VALUES_NOT_SYNCED",
  GAME_NOT_FOUND: "FREE_SPINS_GAME_NOT_FOUND",
  GAME_NOT_SUPPORTED: "FREE_SPINS_GAME_NOT_SUPPORTED",
  STAKE_DERIVED: "FREE_SPINS_STAKE_DERIVED"
} as const

/** Where an issue belongs in the form. `row` issues name a currency and mark that row. */
export type StakeIssueTarget = "games" | "stake" | "row" | "form"

export const stakeIssueTarget = (code: string): StakeIssueTarget => {
  switch (code) {
    case STAKE_ISSUE_CODES.GAMES_REQUIRED:
    case STAKE_ISSUE_CODES.GAME_NOT_FOUND:
    case STAKE_ISSUE_CODES.GAME_NOT_SUPPORTED:
      return "games"
    case STAKE_ISSUE_CODES.STAKE_REQUIRED:
      return "stake"
    case STAKE_ISSUE_CODES.NOT_A_TIER:
    case STAKE_ISSUE_CODES.NOT_SYNCED:
      return "row"
    default:
      return "form"
  }
}

/** The human sentence for one issue. Unknown codes fall back to the code itself, never to silence. */
export const stakeIssueMessage = (issue: StakeValidationIssue, t: TranslateFn): string => {
  const coin = (issue.coin ?? "").toUpperCase()
  switch (issue.code) {
    case STAKE_ISSUE_CODES.GAMES_REQUIRED:
      return t("bonus2.stake.gamesRequired") as string
    case STAKE_ISSUE_CODES.STAKE_REQUIRED:
      return t("bonus2.stake.stakeRequired") as string
    case STAKE_ISSUE_CODES.NOT_A_TIER: {
      const tiers = issue.tiers?.length ? (t("bonus2.stake.tiersSuffix", { tiers: nearestTiers(issue.tiers, issue.value ?? 0).join(", ") }) as string) : ""
      return t("bonus2.stake.notATier", { coin, value: issue.value ?? "", game: issue.game_id ?? "", tiers }) as string
    }
    case STAKE_ISSUE_CODES.NOT_SYNCED:
      return t("bonus2.stake.notSynced", { game: issue.game_id ?? "", coin }) as string
    case STAKE_ISSUE_CODES.GAME_NOT_FOUND:
      return t("bonus2.stake.gameNotFound", { game: issue.game_id ?? "" }) as string
    case STAKE_ISSUE_CODES.GAME_NOT_SUPPORTED:
      return t("bonus2.stake.gameNotSupported", { game: issue.game_id ?? "" }) as string
    case STAKE_ISSUE_CODES.STAKE_DERIVED:
      return coin
        ? (t("bonus2.stake.derivedCoin", { coin }) as string)
        : (t("bonus2.stake.derivedAll") as string)
    default:
      return issue.code
  }
}

/** The issues that mark ONE currency row (so the row can show its own error). */
export const issuesForCurrency = (issues: StakeValidationIssue[], coin: string): StakeValidationIssue[] =>
  issues.filter((i) => stakeIssueTarget(i.code) === "row" && (i.coin ?? "").toUpperCase() === coin.toUpperCase())
